import React from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Link,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface ClientRevenueData {
  clientId: string;
  clientName: string;
  totalRevenue: number;
  serviceCount: number;
  serviceTypes: string[];
  avgContractValue: number;
}

interface ClientRevenueAnalysisProps {
  data: ClientRevenueData[];
  isLoading: boolean;
}

const ClientRevenueAnalysis: React.FC<ClientRevenueAnalysisProps> = ({ data, isLoading }) => {
  const navigate = useNavigate();

  const handleClientClick = (clientId: string) => {
    navigate(`/utilities/clients/${clientId}`);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getRevenueColor = (revenue: number, maxRevenue: number) => {
    const percentage = (revenue / maxRevenue) * 100;
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'default';
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: '#fff' }}>Loading client revenue data...</Typography>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography sx={{ color: '#fff' }}>No client revenue data available</Typography>
      </Paper>
    );
  }

  const maxRevenue = Math.max(...data.map(client => client.totalRevenue));

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 3 }}>
        Top Revenue Clients
      </Typography>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Rank</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Client</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Total Revenue</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Services</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Service Types</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Avg Contract</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((client, index) => (
              <TableRow key={client.clientId} hover>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1rem' }}>
                    #{index + 1}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Link
                    component="button"
                    variant="inherit"
                    onClick={() => handleClientClick(client.clientId)}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontWeight: 'bold',
                      color: '#fff',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: 'none',
                        color: '#e0e0e0'
                      }
                    }}
                  >
                    {client.clientName || 'Unknown Client'}
                  </Link>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}>
                      {formatCurrency(client.totalRevenue)}
                    </Typography>
                    <Chip
                      label={`${Math.round((client.totalRevenue / maxRevenue) * 100)}%`}
                      color={getRevenueColor(client.totalRevenue, maxRevenue)}
                      size="small"
                      sx={{ minWidth: '50px' }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                    {client.serviceCount}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {client.serviceTypes.slice(0, 3).map((serviceType, idx) => (
                      <Chip
                        key={idx}
                        label={serviceType}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: '#fff',
                          borderColor: '#fff',
                          fontSize: '0.7rem'
                        }}
                      />
                    ))}
                    {client.serviceTypes.length > 3 && (
                      <Chip
                        label={`+${client.serviceTypes.length - 3}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: '#fff',
                          borderColor: '#fff',
                          fontSize: '0.7rem'
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                    {formatCurrency(client.avgContractValue)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: '#bbb' }}>
          Showing top {data.length} clients by total revenue
        </Typography>
        <Typography variant="body2" sx={{ color: '#bbb' }}>
          Total Revenue: {formatCurrency(data.reduce((sum, client) => sum + client.totalRevenue, 0))}
        </Typography>
      </Box>
    </Paper>
  );
};

export { ClientRevenueAnalysis };
